import { Icon, Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";

interface ScrollHintProps {
  label: string;
  onClick: () => void;
}

/** Bouncing down-chevron that hints at (and performs) the snap to the next section. */
export function ScrollHint({ label, onClick }: ScrollHintProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full p-2 opacity-70 transition-opacity duration-[var(--transition-quick-ease,200ms)] hover:opacity-100 [filter:drop-shadow(0_0_6px_var(--color-foreground-green-subtle,#01ae66))]"
    >
      <span className="inline-flex animate-bounce">
        <Icon
          kind="ChevronDown"
          size="medium"
          color="--color-foreground-green-subtle"
        />
      </span>
    </button>
  );
}

const FLOW_ICON_COLOR = "--color-foreground-green-subtle";
// Slightly heavier subtitle weight for legibility.
const FLOW_CAPTION_CLASS = "!font-medium";

/** The "what to expect" 3-step flow shown in the prominent Connect-a-source card. */
export function WhatToExpectFlow() {
  return (
    <div className="Onboarding__flow">
      <div className="Onboarding__flow-step">
        <span className="Onboarding__flow-ico">
          <Icon kind="Link" size="medium" color={FLOW_ICON_COLOR} />
        </span>
        <Typography kind="body3-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="ql79RY"
            description="What-to-expect step 1 title"
            defaultMessage="Add a source"
          />
        </Typography>
        <Typography
          kind="body3"
          color="--color-foreground-neutral-medium"
          className={FLOW_CAPTION_CLASS}
        >
          <FormattedMessage
            id="SWHafi"
            description="What-to-expect step 1 caption"
            defaultMessage="Connect a provider like AWS Bedrock or Microsoft Copilot."
          />
        </Typography>
      </div>

      <span className="Onboarding__flow-arrow" aria-hidden="true">
        <Icon
          kind="ArrowRight"
          size="xSmall"
          color="--color-foreground-neutral-subtle"
        />
      </span>

      <div className="Onboarding__flow-step">
        <span className="Onboarding__flow-ico">
          <Icon kind="Time" size="medium" color={FLOW_ICON_COLOR} />
        </span>
        <Typography kind="body3-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="8VGWDq"
            description="What-to-expect step 2 title"
            defaultMessage="Scheduled ingestion"
          />
        </Typography>
        <Typography
          kind="body3"
          color="--color-foreground-neutral-medium"
          className={FLOW_CAPTION_CLASS}
        >
          <FormattedMessage
            id="WfUJqz"
            description="What-to-expect step 2 caption"
            defaultMessage="Zuma syncs automatically at the scheduled time — no manual work."
          />
        </Typography>
      </div>

      <span className="Onboarding__flow-arrow" aria-hidden="true">
        <Icon
          kind="ArrowRight"
          size="xSmall"
          color="--color-foreground-neutral-subtle"
        />
      </span>

      <div className="Onboarding__flow-step">
        <span className="Onboarding__flow-ico">
          <Icon kind="Identities" size="medium" color={FLOW_ICON_COLOR} />
        </span>
        <Typography kind="body3-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="UQ+YWu"
            description="What-to-expect step 3 title"
            defaultMessage="Identities discovered"
          />
        </Typography>
        <Typography
          kind="body3"
          color="--color-foreground-neutral-medium"
          className={FLOW_CAPTION_CLASS}
        >
          <FormattedMessage
            id="xAmBPN"
            description="What-to-expect step 3 caption"
            defaultMessage="AI agents and non-human identities appear in your posture."
          />
        </Typography>
      </div>
    </div>
  );
}
